import React, { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import HeaderTwo from '../common/header/HeaderTwo';
import FooterOne from '../common/footer/FooterOne';
import { FilterSidebar, ListingCard, MapView } from '../components/directory';
import { listings, searchListings, getListingsByType } from '../data';
import { Grid3X3, List, Map, SlidersHorizontal, X, ChevronDown } from 'lucide-react';

const ListingsOne = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState('grid');
  const [showMap, setShowMap] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [sortBy, setSortBy] = useState('featured');

  // Filter state
  const [filters, setFilters] = useState({
    search: searchParams.get('q') || '',
    categories: searchParams.get('category') ? [searchParams.get('category')] : [],
    products: [],
    distance: 'any',
    features: [],
  });

  // Apply filters
  const filteredListings = useMemo(() => {
    let result = [...listings];

    // Search filter
    if (filters.search) {
      result = searchListings(filters.search);
    }

    // Category filter
    if (filters.categories.length > 0) {
      result = result.filter(listing =>
        filters.categories.includes(listing.type)
      );
    }

    // Products filter
    if (filters.products.length > 0) {
      result = result.filter(listing =>
        listing.products.some(product =>
          filters.products.some(fp =>
            product.toLowerCase().includes(fp.toLowerCase())
          )
        )
      );
    }

    // Features filter
    if (filters.features.length > 0) {
      result = result.filter(listing => {
        const listingFeatures = listing.tags || [];
        return filters.features.some(feature => {
          if (feature === 'organic') {
            return listingFeatures.some(t => t.includes('organic'));
          }
          if (feature === 'family-owned') {
            return listingFeatures.some(t => t.includes('family'));
          }
          if (feature === 'delivery') {
            return listingFeatures.some(t => t.includes('delivery') || t.includes('csa'));
          }
          return listingFeatures.includes(feature);
        });
      });
    }

    // Sort
    switch (sortBy) {
      case 'featured':
        result.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'reviews':
        result.sort((a, b) => b.reviewCount - a.reviewCount);
        break;
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        break;
    }

    return result;
  }, [filters, sortBy]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      categories: [],
      products: [],
      distance: 'any',
      features: [],
    });
    setSearchParams({});
  };

  const activeFilterCount =
    filters.categories.length +
    filters.products.length +
    filters.features.length +
    (filters.distance !== 'any' ? 1 : 0) +
    (filters.search ? 1 : 0);

  return (
    <>
      <HeaderTwo />

      {/* Page Header */}
      <section style={styles.pageHeader}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 col-12">
              <nav style={styles.breadcrumb}>
                <Link to="/" style={styles.breadcrumbLink}>Home</Link>
                <span style={styles.breadcrumbSep}>/</span>
                <span style={styles.breadcrumbCurrent}>Browse Listings</span>
              </nav>
              <h1 style={styles.pageTitle}>
                {filters.categories.length === 1
                  ? `${filters.categories[0].charAt(0).toUpperCase() + filters.categories[0].slice(1)}s`
                  : 'All Listings'
                }
              </h1>
              <p style={styles.resultCount}>
                {filteredListings.length} {filteredListings.length === 1 ? 'listing' : 'listings'} found
              </p>
            </div>
            <div className="col-lg-6 col-12">
              <div style={styles.headerActions}>
                {/* View Toggle */}
                <div style={styles.viewToggle}>
                  <button
                    style={{
                      ...styles.viewBtn,
                      ...(viewMode === 'grid' ? styles.viewBtnActive : {}),
                    }}
                    onClick={() => { setViewMode('grid'); setShowMap(false); }}
                    title="Grid View"
                  >
                    <Grid3X3 size={18} />
                  </button>
                  <button
                    style={{
                      ...styles.viewBtn,
                      ...(viewMode === 'list' ? styles.viewBtnActive : {}),
                    }}
                    onClick={() => { setViewMode('list'); setShowMap(false); }}
                    title="List View"
                  >
                    <List size={18} />
                  </button>
                  <button
                    style={{
                      ...styles.viewBtn,
                      ...(showMap ? styles.viewBtnActive : {}),
                    }}
                    onClick={() => setShowMap(!showMap)}
                    title="Map View"
                  >
                    <Map size={18} />
                  </button>
                </div>

                {/* Sort Dropdown */}
                <div style={styles.sortWrap}>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    style={styles.sortSelect}
                  >
                    <option value="featured">Featured First</option>
                    <option value="rating">Highest Rated</option>
                    <option value="reviews">Most Reviews</option>
                    <option value="name">Name A-Z</option>
                  </select>
                  <ChevronDown size={16} style={styles.sortIcon} />
                </div>

                {/* Mobile Filter Toggle */}
                <button
                  style={styles.mobileFilterBtn}
                  onClick={() => setShowMobileFilters(true)}
                >
                  <SlidersHorizontal size={18} />
                  Filters
                  {activeFilterCount > 0 && (
                    <span style={styles.filterBadge}>{activeFilterCount}</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section style={styles.mainSection}>
        <div className="container">
          <div className="row">
            {/* Desktop Sidebar */}
            <div className="col-lg-3 d-none d-lg-block">
              <div style={styles.sidebarWrap}>
                <FilterSidebar
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={handleClearFilters}
                />
              </div>
            </div>

            {/* Listings Grid/List */}
            <div className="col-lg-9 col-12">
              {/* Active Filters */}
              {activeFilterCount > 0 && (
                <div style={styles.activeFilters}>
                  {filters.search && (
                    <span style={styles.filterChip}>
                      Search: "{filters.search}"
                      <button
                        style={styles.chipClose}
                        onClick={() => setFilters({ ...filters, search: '' })}
                      >
                        <X size={14} />
                      </button>
                    </span>
                  )}
                  {filters.categories.map(cat => (
                    <span key={cat} style={styles.filterChip}>
                      {cat}
                      <button
                        style={styles.chipClose}
                        onClick={() => setFilters({
                          ...filters,
                          categories: filters.categories.filter(c => c !== cat)
                        })}
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                  {filters.products.map(prod => (
                    <span key={prod} style={styles.filterChip}>
                      {prod}
                      <button
                        style={styles.chipClose}
                        onClick={() => setFilters({
                          ...filters,
                          products: filters.products.filter(p => p !== prod)
                        })}
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                  <button style={styles.clearAllBtn} onClick={handleClearFilters}>
                    Clear All
                  </button>
                </div>
              )}

              {/* Map View */}
              {showMap && (
                <div style={styles.mapContainer}>
                  <MapView listings={filteredListings} />
                </div>
              )}

              {/* Listings */}
              {!showMap && (
                <>
                  {filteredListings.length === 0 ? (
                    <div style={styles.noResults}>
                      <h3 style={styles.noResultsTitle}>No listings found</h3>
                      <p style={styles.noResultsText}>
                        Try adjusting your filters or search terms
                      </p>
                      <button style={styles.clearFiltersBtn} onClick={handleClearFilters}>
                        Clear All Filters
                      </button>
                    </div>
                  ) : (
                    <div className="row">
                      {viewMode === 'grid' ? (
                        filteredListings.map((listing, index) => (
                          <div className="col-md-6 col-xl-4 col-12 mb-4" key={listing.id}>
                            <div className="wow fadeInUp" data-wow-delay={`${(index % 6) * 50}ms`}>
                              <ListingCard listing={listing} variant="grid" />
                            </div>
                          </div>
                        ))
                      ) : (
                        filteredListings.map((listing, index) => (
                          <div className="col-12 mb-3" key={listing.id}>
                            <div className="wow fadeInUp" data-wow-delay={`${(index % 4) * 50}ms`}>
                              <ListingCard listing={listing} variant="list" />
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Filter Overlay */}
      {showMobileFilters && (
        <div style={styles.mobileFilterOverlay}>
          <div style={styles.mobileFilterPanel}>
            <div style={styles.mobileFilterHeader}>
              <h3 style={styles.mobileFilterTitle}>Filters</h3>
              <button
                style={styles.mobileFilterClose}
                onClick={() => setShowMobileFilters(false)}
              >
                <X size={24} />
              </button>
            </div>
            <div style={styles.mobileFilterContent}>
              <FilterSidebar
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                isMobile={true}
              />
            </div>
            <div style={styles.mobileFilterFooter}>
              <button
                style={styles.applyFiltersBtn}
                onClick={() => setShowMobileFilters(false)}
              >
                Show {filteredListings.length} Results
              </button>
            </div>
          </div>
        </div>
      )}

      <FooterOne />
    </>
  );
};

const styles = {
  pageHeader: {
    padding: '120px 0 30px',
    backgroundColor: 'var(--thm-bg-sage)',
    borderBottom: '1px solid rgba(45, 90, 39, 0.1)',
  },
  breadcrumb: {
    marginBottom: '12px',
  },
  breadcrumbLink: {
    color: 'var(--thm-base)',
    textDecoration: 'none',
    fontSize: '14px',
    fontFamily: 'var(--thm-font-2)',
  },
  breadcrumbSep: {
    margin: '0 8px',
    color: 'var(--thm-gray)',
  },
  breadcrumbCurrent: {
    color: 'var(--thm-gray)',
    fontSize: '14px',
    fontFamily: 'var(--thm-font-2)',
  },
  pageTitle: {
    fontSize: '32px',
    fontWeight: '700',
    color: 'var(--thm-black)',
    fontFamily: 'var(--thm-font)',
    marginBottom: '8px',
  },
  resultCount: {
    fontSize: '16px',
    color: 'var(--thm-gray)',
    fontFamily: 'var(--thm-font-2)',
    margin: 0,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    flexWrap: 'wrap',
  },
  viewToggle: {
    display: 'flex',
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  viewBtn: {
    width: '40px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '6px',
    color: 'var(--thm-gray)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  viewBtnActive: {
    backgroundColor: 'var(--thm-base)',
    color: '#fff',
  },
  sortWrap: {
    position: 'relative',
  },
  sortSelect: {
    appearance: 'none',
    padding: '10px 40px 10px 16px',
    backgroundColor: '#fff',
    border: '1px solid rgba(45, 90, 39, 0.2)',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: 'var(--thm-font-2)',
    color: 'var(--thm-black)',
    cursor: 'pointer',
    minWidth: '160px',
  },
  sortIcon: {
    position: 'absolute',
    right: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'var(--thm-gray)',
    pointerEvents: 'none',
  },
  mobileFilterBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    backgroundColor: '#fff',
    border: '1px solid rgba(45, 90, 39, 0.2)',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: 'var(--thm-font-2)',
    color: 'var(--thm-black)',
    cursor: 'pointer',
  },
  filterBadge: {
    backgroundColor: 'var(--thm-base)',
    color: '#fff',
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '12px',
    fontWeight: '600',
  },
  mainSection: {
    padding: '40px 0 80px',
    backgroundColor: 'var(--thm-bg-cream)',
    minHeight: '60vh',
  },
  sidebarWrap: {
    position: 'sticky',
    top: '100px',
  },
  activeFilters: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '24px',
    alignItems: 'center',
  },
  filterChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    backgroundColor: 'rgba(45, 90, 39, 0.1)',
    color: 'var(--thm-base)',
    borderRadius: '20px',
    fontSize: '13px',
    fontFamily: 'var(--thm-font-2)',
    textTransform: 'capitalize',
  },
  chipClose: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '18px',
    height: '18px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '50%',
    cursor: 'pointer',
    color: 'var(--thm-base)',
    padding: 0,
  },
  clearAllBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    color: 'var(--thm-accent)',
    fontSize: '13px',
    fontFamily: 'var(--thm-font-2)',
    fontWeight: '600',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  mapContainer: {
    height: '500px',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    marginBottom: '24px',
  },
  noResults: {
    textAlign: 'center',
    padding: '60px 20px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
  },
  noResultsTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: 'var(--thm-black)',
    fontFamily: 'var(--thm-font)',
    marginBottom: '12px',
  },
  noResultsText: {
    fontSize: '16px',
    color: 'var(--thm-gray)',
    fontFamily: 'var(--thm-font-2)',
    marginBottom: '24px',
  },
  clearFiltersBtn: {
    padding: '12px 24px',
    backgroundColor: 'var(--thm-base)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: 'var(--thm-font-2)',
    cursor: 'pointer',
  },
  mobileFilterOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 9999,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  mobileFilterPanel: {
    width: '100%',
    maxWidth: '380px',
    backgroundColor: '#fff',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  mobileFilterHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px',
    borderBottom: '1px solid rgba(0,0,0,0.1)',
  },
  mobileFilterTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: 'var(--thm-black)',
    fontFamily: 'var(--thm-font)',
    margin: 0,
  },
  mobileFilterClose: {
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '8px',
    color: 'var(--thm-gray)',
    cursor: 'pointer',
  },
  mobileFilterContent: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px',
  },
  mobileFilterFooter: {
    padding: '20px',
    borderTop: '1px solid rgba(0,0,0,0.1)',
  },
  applyFiltersBtn: {
    width: '100%',
    padding: '14px 24px',
    backgroundColor: 'var(--thm-base)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    fontFamily: 'var(--thm-font-2)',
    cursor: 'pointer',
  },
};

// Add CSS media query styles
const mediaStyles = `
  @media (min-width: 992px) {
    .mobile-filter-btn { display: none !important; }
  }
  @media (max-width: 991px) {
    .header-actions { justify-content: space-between !important; }
    .view-toggle { order: 2; }
    .sort-wrap { order: 3; flex: 1; }
    .mobile-filter-btn { order: 1; }
  }
`;

export default ListingsOne;
